import API from '@/api/AxiosInstance'

export const GUIDED_RECORDING_CONSENT_VERSION = '2026-09-18'
export const GUIDED_RECORDING_FINALIZATION_RETRYABLE_CODE = 'guided_recording_finalization_retryable'

export const isGuidedRecordingFinalizationRetryable = (error) => (
  error?.response?.status === 503
  && error?.response?.data?.error?.code === GUIDED_RECORDING_FINALIZATION_RETRYABLE_CODE
)

export const toGuidedRecordingConsentConfiguration = (consentRequired) => {
  const required = consentRequired === true

  return {
    consentRequired: required,
    consentStatementVersion: required
      ? GUIDED_RECORDING_CONSENT_VERSION
      : null,
  }
}
const toDateOnly = (value) => {
  if (!(value instanceof Date)) {
    return value
  }

  return [
    value.getFullYear(),
    String(value.getMonth() + 1).padStart(2, '0'),
    String(value.getDate()).padStart(2, '0'),
  ].join('-')
}


export const toGuidedRecordingExpiryConfiguration = (expiresAt, includeEmptyExpiry = false) => {
  if (expiresAt === null || expiresAt === undefined || expiresAt === '') {
    return includeEmptyExpiry ? { expiresAt: null } : {}
  }

  return {
    expiresAt: toDateOnly(expiresAt),
  }
}

const GUIDED_RECORDING_AUTOCUE_TELEMETRY_REASONS = Object.freeze({
  autocue_open: Object.freeze(['opened', 'hidden']),
  autocue_alignment: Object.freeze(['left', 'center', 'right']),
  autocue_control_failure: Object.freeze([
    'start',
    'pause',
    'resume',
    'restart',
    'no_script',
    'reduced_motion',
    'missing_scroller',
  ]),
})

const isAllowedAutocueTelemetry = (event, reason) => (
  typeof event === 'string'
  && typeof reason === 'string'
  && Object.hasOwn(GUIDED_RECORDING_AUTOCUE_TELEMETRY_REASONS, event)
  && GUIDED_RECORDING_AUTOCUE_TELEMETRY_REASONS[event].includes(reason)
)

const guidedSessionOptions = (sessionCapability, options = {}) => ({
  ...options,
  headers: {
    ...options.headers,
    'vb-guided-recording-token': sessionCapability,
  },
})

const guidedRecoveryOptions = (recoveryCredential, options = {}) => ({
  ...options,
  headers: {
    ...options.headers,
    'vb-guided-recording-recovery-token': recoveryCredential,
  },
})

const unwrapGuidedRecordingResponse = (response) => response.data

const postGuidedRecordingAutocueTelemetry = (
  code,
  sessionCapability,
  event,
  reason,
) => {
  if (!code || typeof code !== 'string'
    || !sessionCapability || typeof sessionCapability !== 'string'
    || !isAllowedAutocueTelemetry(event, reason)) {
    return Promise.resolve(null)
  }

  try {
    return API.post(
      `guided-recordings/${code}/session/autocue-telemetry`,
      { event, reason },
      guidedSessionOptions(sessionCapability),
    )
      .then(unwrapGuidedRecordingResponse)
      .catch(() => null)
  } catch {
    return Promise.resolve(null)
  }
}

export default {
  getPublicInvitation: function (code) {
    return API.get(`guided-recordings/${code}`).then(
      unwrapGuidedRecordingResponse
    )
  },

  startPublicSession: function (code, data, browserContextId) {
    const body = browserContextId ? { ...data, browserContextId } : data
    return API.post(`guided-recordings/${code}/sessions`, body).then(
      unwrapGuidedRecordingResponse
    )
  },

  getPublicSession: function (code, sessionCapability) {
    return API.get(
      `guided-recordings/${code}/session`,
      guidedSessionOptions(sessionCapability)
    ).then(unwrapGuidedRecordingResponse)
  },

  recordAutocueTelemetry: function (code, sessionCapability, event, reason) {
    return postGuidedRecordingAutocueTelemetry(code, sessionCapability, event, reason)
  },

  resumePublicSession: function (code, recoveryCredential, browserContextId) {
    return API.post(
      `guided-recordings/${code}/session/resume`,
      { browserContextId },
      guidedRecoveryOptions(recoveryCredential)
    ).then(unwrapGuidedRecordingResponse)
  },

  getUploadStatus: function (code, sessionCapability) {
    return API.get(
      `guided-recordings/${code}/session/upload-status`,
      guidedSessionOptions(sessionCapability)
    ).then(unwrapGuidedRecordingResponse)
  },

  overrideAudioWarning: function (code, sessionCapability) {
    return API.post(
      `guided-recordings/${code}/session/audio/warning-override`,
      {},
      guidedSessionOptions(sessionCapability)
    ).then(unwrapGuidedRecordingResponse)
  },

  continueAudioUnavailable: function (code, sessionCapability) {
    return API.post(
      `guided-recordings/${code}/session/audio/unavailable-continue`,
      {},
      guidedSessionOptions(sessionCapability)
    ).then(unwrapGuidedRecordingResponse)
  },

  recordAudioAgain: function (code, sessionCapability) {
    return API.post(
      `guided-recordings/${code}/session/audio/record-again`,
      {},
      guidedSessionOptions(sessionCapability)
    ).then(unwrapGuidedRecordingResponse)
  },

  nextPublicSessionQuestion: function (code, sessionCapability) {
    return API.post(
      `guided-recordings/${code}/session/next`,
      {},
      guidedSessionOptions(sessionCapability)
    ).then(unwrapGuidedRecordingResponse)
  },

  previousPublicSessionQuestion: function (code, sessionCapability) {
    return API.post(
      `guided-recordings/${code}/session/previous`,
      {},
      guidedSessionOptions(sessionCapability)
    ).then(unwrapGuidedRecordingResponse)
  },

  selectPublicSessionQuestion: function (code, sessionCapability, questionOrder) {
    return API.post(
      `guided-recordings/${code}/session/questions/${questionOrder}/select`,
      {},
      guidedSessionOptions(sessionCapability)
    ).then(unwrapGuidedRecordingResponse)
  },

  skipPublicSessionQuestion: function (code, sessionCapability) {
    return API.post(
      `guided-recordings/${code}/session/skip`,
      {},
      guidedSessionOptions(sessionCapability)
    ).then(unwrapGuidedRecordingResponse)
  },

  acceptTakeLocally: function (code, sessionCapability, order, idempotencyKey) {
    return API.post(
      `guided-recordings/${code}/session/questions/${order}/take`,
      { idempotencyKey },
      guidedSessionOptions(sessionCapability)
    ).then(unwrapGuidedRecordingResponse)
  },

  createAcceptedTake: function (data, sessionCapability, options) {
    return API.post(
      'uploadaccess/files/upload',
      data,
      guidedSessionOptions(sessionCapability, options)
    ).then(unwrapGuidedRecordingResponse)
  },

  completeAcceptedTake: function (data, sessionCapability) {
    return API.post(
      'uploadaccess/files/multipart-complete',
      data,
      guidedSessionOptions(sessionCapability)
    )
  },

  abortAcceptedTake: function (data, sessionCapability) {
    return API.post(
      'uploadaccess/files/multipart-abort',
      data,
      guidedSessionOptions(sessionCapability)
    )
  },

  cancelAcceptedTake: function (data, sessionCapability) {
    return API.post(
      'uploadaccess/files/cancel',
      data,
      guidedSessionOptions(sessionCapability)
    )
  },

  createInvitation: function (projectNodeId, data) {
    return API.post(`nodes/${projectNodeId}/guided-recordings`, data).then(
      unwrapGuidedRecordingResponse
    )
  },

  getInvitations: function (projectNodeId) {
    return API.get(`nodes/${projectNodeId}/guided-recordings`).then(
      unwrapGuidedRecordingResponse
    )
  },

  getInvitation: function (projectNodeId, invitationId) {
    return API.get(
      `nodes/${projectNodeId}/guided-recordings/${invitationId}`
    ).then(unwrapGuidedRecordingResponse)
  },

  updateInvitation: function (projectNodeId, invitationId, data) {
    return API.patch(
      `nodes/${projectNodeId}/guided-recordings/${invitationId}`,
      data
    ).then(unwrapGuidedRecordingResponse)
  },

  deleteInvitation: function (projectNodeId, invitationId) {
    return API.delete(
      `nodes/${projectNodeId}/guided-recordings/${invitationId}`
    ).then(unwrapGuidedRecordingResponse)
  },

  createQuestion: function (projectNodeId, invitationId, data) {
    return API.post(
      `nodes/${projectNodeId}/guided-recordings/${invitationId}/questions`,
      data
    ).then(unwrapGuidedRecordingResponse)
  },

  updateQuestion: function (projectNodeId, invitationId, questionId, data) {
    return API.patch(
      `nodes/${projectNodeId}/guided-recordings/${invitationId}/questions/${questionId}`,
      data
    ).then(unwrapGuidedRecordingResponse)
  },

  deleteQuestion: function (projectNodeId, invitationId, questionId, expectedInvitationUpdatedAt) {
    return API.delete(
      `nodes/${projectNodeId}/guided-recordings/${invitationId}/questions/${questionId}`,
      { data: { expectedInvitationUpdatedAt } }
    ).then(unwrapGuidedRecordingResponse)
  },

  reorderQuestions: function (projectNodeId, invitationId, questionIds, expectedInvitationUpdatedAt) {
    return API.put(
      `nodes/${projectNodeId}/guided-recordings/${invitationId}/questions/order`,
      { questionIds, expectedInvitationUpdatedAt }
    ).then(unwrapGuidedRecordingResponse)
  },

}
